import { useState } from "react";
import { useHomeownerAuth } from "./useHomeownerAuth";
import HomeownerGate from "./HomeownerGate";
import { authTriple, hoSubmitFeedback } from "../../lib/homeownerRpc";

const GOOGLE_REVIEW_URL = "https://g.page/r/CWo6THey4v2CEBI/review";
const STARS = [1, 2, 3, 4, 5] as const;

export default function FeedbackPage() {
  const auth = useHomeownerAuth();
  const { deal } = auth;

  const [rating, setRating] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!deal || !rating) return;
    setSubmitting(true);

    // Save feedback via token-validated RPC (no direct deals write).
    await hoSubmitFeedback(authTriple(deal), rating, comment);

    setSubmitted(true);
    setSubmitting(false);
  };

  // After submit: 5 stars → show Google review prompt, 1-4 → thank you
  if (submitted && deal) {
    if (rating === 5) {
      return (
        <HomeownerGate {...auth} title="Thank You!">
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 shadow-sm text-center">
              <div className="text-5xl mb-3">⭐</div>
              <h1 className="text-xl font-bold text-bardin-ink mb-2">
                Thank you, {deal.homeowner_name?.split(" ")[0]}!
              </h1>
              <p className="text-gray-600 mb-6">
                We're thrilled you had a 5-star experience! Would you mind sharing
                that on Google? It helps other homeowners find quality roofing.
              </p>
              <a
                href={GOOGLE_REVIEW_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-bardin-orange text-white font-semibold rounded-lg px-8 py-3 hover:bg-bardin-orange/90 transition"
              >
                Leave a Google Review →
              </a>
              <p className="text-xs text-gray-400 mt-4">
                It only takes 30 seconds and means the world to our team.
              </p>
            </div>
          </div>
        </HomeownerGate>
      );
    }

    return (
      <HomeownerGate {...auth} title="Thank You">
        <div className="bg-white rounded-xl p-6 shadow-sm text-center">
          <div className="text-5xl mb-3">🙏</div>
          <h1 className="text-xl font-bold text-bardin-ink mb-2">Thank you for your feedback</h1>
          <p className="text-gray-600">
            We appreciate you letting us know. Your Restore Manager{" "}
            {deal.owner_name ? `(${deal.owner_name}) ` : ""}
            will follow up with you personally.
          </p>
        </div>
      </HomeownerGate>
    );
  }

  return (
    <HomeownerGate {...auth} title="How Did We Do?">
      {deal && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h1 className="text-lg font-bold text-bardin-ink text-center mb-1">
              How was your experience?
            </h1>
            <p className="text-sm text-gray-500 text-center mb-6">
              {deal.property_street}
            </p>

            {/* Star rating */}
            <div className="flex justify-center gap-3 mb-6">
              {STARS.map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className={`text-4xl transition-transform hover:scale-110 ${
                    rating && star <= rating ? "" : "opacity-30 grayscale"
                  }`}
                >
                  ⭐
                </button>
              ))}
            </div>

            {rating && (
              <div className="text-center text-sm font-medium text-bardin-ink mb-4">
                {rating === 5
                  ? "Excellent!"
                  : rating === 4
                  ? "Great!"
                  : rating === 3
                  ? "Good"
                  : rating === 2
                  ? "Fair"
                  : "We're sorry to hear that"}
              </div>
            )}

            {/* Optional comment */}
            {rating && (
              <>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={
                    rating >= 4
                      ? "Anything you'd like to add? (optional)"
                      : "We'd love to know how we can improve..."
                  }
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent resize-none"
                  rows={3}
                />

                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="w-full mt-4 bg-bardin-orange text-white font-semibold rounded-lg py-3 hover:bg-bardin-orange/90 transition disabled:opacity-50"
                >
                  {submitting ? "Submitting..." : "Submit Feedback"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </HomeownerGate>
  );
}
